import { format } from '@rental/i18n'
import { FilePicker } from '@rental/ui'
import { Camera } from 'lucide-react'
import { forwardRef, useEffect, useState } from 'react'
import { AUTH_MODE } from '../api'
import { filePath } from '../files'
import { MediaPhoto } from '../files-ui'
import { useLocale } from '../i18n'
import { photoToJpeg } from '../photos/jpeg'
import { type ModelPicture, modelLabel, modelPicture, type StaffModel } from './models'

const FRAME = 'aspect-4/3 w-full rounded-sm'

/** A picture as the screen shows it: a photo taken here, the website's own, or a quiet blank. */
export function PictureOf({ picture, alt }: { picture: ModelPicture; alt: string }) {
  if (picture.kind === 'media') {
    // A1, as the inspection photos: the web app's image carries the cookies;
    // the iPad app fetches the bytes with its credentials and shows them from memory.
    return AUTH_MODE === 'cookie' ? (
      <img
        src={filePath({ kind: 'photo', mediaId: picture.mediaId })}
        alt={alt}
        loading="lazy"
        className={`${FRAME} border border-rule bg-bg object-cover`}
      />
    ) : (
      <MediaPhoto mediaId={picture.mediaId} alt={alt} className={`${FRAME} object-cover`} />
    )
  }
  if (picture.kind === 'site') {
    // The website's cut-outs: the whole model on a light ground, as the website shows them.
    return (
      <img
        src={picture.src}
        alt={alt}
        loading="lazy"
        className={`${FRAME} border border-rule bg-bg object-contain p-2`}
      />
    )
  }
  return <div className={`${FRAME} border border-dashed border-rule bg-bg`} />
}

/** A model's picture, named for a screen reader by its model. */
export function ModelPictureOf({ model }: { model: StaffModel }) {
  const { messages: m } = useLocale()
  return (
    <PictureOf
      picture={modelPicture(model)}
      alt={format(m.models.photoOf, { model: modelLabel(model, m.transmissions) })}
    />
  )
}

export type ChosenPhoto = { blob: Blob; url: string } | null

/**
 * A photo taken or chosen on this device, re-encoded as JPEG (the app's own
 * way, photos/jpeg.ts) and previewed before anything is sent. The words say,
 * before a photo is taken, that it will be on the website.
 */
export const PhotoField = forwardRef<
  HTMLInputElement,
  {
    label: string
    chosen: ChosenPhoto
    error: string | null
    onChosen: (photo: ChosenPhoto) => void
    onUnreadable: () => void
  }
>(function PhotoField({ label, chosen, error, onChosen, onUnreadable }, ref) {
  const { messages: m } = useLocale()
  const [reading, setReading] = useState(false)
  // The preview's memory is freed when another photo replaces it or the field goes.
  useEffect(() => () => (chosen ? URL.revokeObjectURL(chosen.url) : undefined), [chosen])

  async function choose(file: File | undefined) {
    onChosen(null)
    if (!file) return
    setReading(true)
    const jpeg = await photoToJpeg(file)
    setReading(false)
    if (!jpeg) {
      onUnreadable()
      return
    }
    onChosen({ blob: jpeg, url: URL.createObjectURL(jpeg) })
  }

  return (
    <div className="flex flex-col gap-3">
      <FilePicker
        ref={ref}
        label={label}
        icon={<Camera aria-hidden="true" className="size-6" />}
        hint={m.models.photoHint}
        error={error}
        busy={reading}
        className="rounded-sm"
        accept="image/*"
        capture="environment"
        onChange={(event) => void choose(event.target.files?.[0])}
      />
      {chosen ? (
        <img
          src={chosen.url}
          alt={m.models.preview}
          className="aspect-4/3 w-full max-w-xs rounded-sm border border-rule bg-bg object-cover"
        />
      ) : null}
    </div>
  )
})
